/**
 * Stock counts and variance.
 *
 * The variance panel is the reason the stock-balance mirror exists: what the RFID sweep found,
 * against what SQL Accounting believes. It states the age of the balance it compared against,
 * because a count measured against a stale balance is an artefact rather than a variance — and
 * that distinction is the difference between "we lost 40 mattresses" and "our data is old".
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';

import { api, qty } from '../lib/api';
import { businessDate, MYT } from '../lib/time';
import { Card, Table, Loading, Empty, cell, when } from '../components/ui';

type Count = {
  bill_no: string;
  dept_code: string;
  dept_name: string | null;
  planned_date: string | null;
  sl_created_at: string | null;
  status: number | null;
  operator: string | null;
  counted_qty: string;
  skus: number;
};

type VarianceRow = {
  sku: string;
  description: string | null;
  counted: string;
  system_qty: string;
  /** SQL when the count started — present when System is the SUBMIT reading. */
  system_at_start?: string;
  variance: string;
  /** 1 when the handheld actually read this SKU. 0 rows are "never scanned", not losses. */
  was_counted: number;
  /**
   * The tag codes a row was rolled up from (a colour, or the pieces of a set), or why a piece
   * stands alone. Null when the tag carried the accounting item code itself.
   */
  counted_as?: string | null;
  /** SQL Accounting stock group (`TM`, `BF`…). */
  stockgroup?: string | null;
};

/** One tracked location's standing, from its most recent SUBMITTED count. */
type LocationVariance = {
  location: string;
  locationName: string | null;
  billNo: string | null;
  countedAt: string | null;
  snapshotDate: string | null;
  snapshotAgeDays: number | null;
  skusAtLocation: number;
  skusCounted: number;
  skusDisagreeing: number;
  counted: string;
  systemQty: string;
  variance: string;
};

/** Smartlink header status. 3 is submit-FAILED and is not in their reference docs. */
const STATUS: Record<number, { label: string; cls: string }> = {
  1: { label: 'pending', cls: 'bg-slate-100 text-slate-600' },
  2: { label: 'in progress', cls: 'bg-sky-light/30 text-brand-800' },
  3: { label: 'submit failed', cls: 'bg-alert/20 text-alert' },
  10: { label: 'complete', cls: 'bg-emerald-50 text-emerald-700' },
};

export default function StockCount() {
  /**
   * A count's variance is reachable two ways, and the URL decides which.
   *
   * `/rfid/stock-count`            — the list, with the variance expanding below a selection
   * `/rfid/stock-count/:billNo`    — that one count's variance ALONE
   *
   * The second exists because the counts table is long: expanding a panel underneath it meant
   * scrolling back past every row to read the result. The Variance buttons are real links, so
   * they open in a tab (or a new window, or get copied and sent to someone) like any other link.
   */
  const { billNo } = useParams<{ billNo?: string }>();
  const standalone = billNo !== undefined;
  const selected = standalone ? (billNo ?? null) : null;

  /**
   * Location and date filters for the counts list (LSK, 2026-10-31). Kept in the URL, so a
   * filtered list survives a refresh and can be sent as a link. Filtered on the server, so an
   * older count is still reachable rather than only the newest 100 being searched.
   */
  const [params, setParams] = useSearchParams();
  const filter = {
    location: params.get('location') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
  };
  const setFilter = (key: keyof typeof filter, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const filterQuery = new URLSearchParams(
    Object.entries(filter).filter(([, v]) => v !== ''),
  ).toString();

  const counts = useQuery({
    queryKey: ['stock-counts', filterQuery],
    queryFn: () =>
      api.get<{
        counts: Count[];
        truncated?: boolean;
        limit?: number;
        locations?: Array<{ dept_code: string; dept_name: string | null }>;
      }>('/api/v1/stock-count' + (filterQuery ? `?${filterQuery}` : '')),
    refetchInterval: 60_000,
  });

  /**
   * Which balance the report is measured against.
   *
   * `default`  — the server's choice: SQL as it stood when this count was created or started
   *              (LSK, 2026-10-31, countBaseline.ts), or last night's close for a count from before
   *              those readings were taken.
   * `snapshot` — last night's 02:00 close.
   * `live`     — read from SQL Accounting now. ~30s for HQ1. LSK asked for this on 2026-09-11.
   */
  const [basis, setBasis] = useState<'default' | 'snapshot' | 'live'>('default');
  const basisQuery = basis === 'default' ? '' : `?basis=${basis}`;

  const variance = useQuery({
    queryKey: ['variance', selected, basis],
    enabled: selected !== null,
    queryFn: () =>
      api.get<{
        snapshotDate: string | null;
        snapshotAgeDays?: number;
        note?: string;
        rows: VarianceRow[];
        totals?: {
          countedSkus: number;
          countedWithVariance: number;
          countedMatching: number;
          over: number;
          short: number;
          notCounted: number;
          zeroBothHidden?: number;
        };
        truncated?: { shown: number; total: number } | null;
        count: { dept_code: string; dept_name: string | null; counted_qty: string };
        basis?: 'snapshot' | 'live' | 'count-start';
        baselineAt?: string;
        baselineTrigger?: 'created' | 'started' | 'submitted';
        baselineStartAt?: string | null;
        liveReadAt?: string;
        liveAsAt?: string;
      }>(`/api/v1/stock-count/${encodeURIComponent(selected ?? '')}/variance` + basisQuery),
  });

  const v = variance.data;
  /** System is the submit reading and a start reading is held beside it. */
  const withStart = v?.basis === 'count-start' && Boolean(v.baselineStartAt);
  /** The location this count covers — `HQ1`, not the count number. */
  const location = v?.count?.dept_code ?? null;

  /**
   * "What does SQL say RIGHT NOW?"
   *
   * LSK asked why a quantity edited at 08:15 does not appear in this report at 08:30. The System
   * column is the 02:00 snapshot — the previous day's closing balance — so it cannot. A full
   * refresh is ~4.6 minutes against LSK's bridge, but one location is ~2.3 seconds, so it is a
   * button rather than a schedule. Disabled by default: it costs a live call, so it happens when
   * someone actually asks.
   */

  const byLocation = useQuery({
    queryKey: ['stock-count', 'by-location'],
    queryFn: () => api.get<{ locations: LocationVariance[] }>('/api/v1/stock-count/by-location'),
  });

  return (
    <div className="space-y-6">
      {/* On the standalone page the reader came for ONE count; the two list cards would just be
          the scrolling this change exists to remove. */}
      {standalone && (
        <a href="/rfid/stock-count" className="inline-block text-sm text-brand-700 hover:underline">
          &larr; All stock counts
        </a>
      )}

      {!standalone && (
        <>
          <Card
            title="Variance by location"
            subtitle="Each location against SQL Accounting, from its most recent SUBMITTED count. Only SKUs that were actually scanned are compared — a location is never marked short for stock nobody looked at."
          >
            {byLocation.isLoading ? (
              <Loading />
            ) : (
              <Table
                head={[
                  'Location',
                  'Last count',
                  'SKUs counted',
                  'Counted',
                  'System',
                  'Variance',
                  'SKUs disagreeing',
                  'Compared against',
                ]}
                rows={(byLocation.data?.locations ?? []).map((l) => {
                  const uncounted = l.billNo === null;
                  const varNum = Number(l.variance);
                  return [
                    cell(
                      l.location,
                      <span>
                        <span className="font-mono text-xs font-semibold">{l.location}</span>
                        <span className="ml-2 text-xs text-slate-500">{l.locationName ?? ''}</span>
                      </span>,
                    ),
                    uncounted
                      ? cell('', <span className="text-slate-400">not yet counted</span>)
                      : cell(
                          l.countedAt ?? '',
                          <span>
                            <span className="font-mono text-xs">{l.billNo}</span>
                            <span className="ml-2 text-xs text-slate-500">
                              {l.countedAt ? businessDate(l.countedAt) : ''}
                            </span>
                          </span>,
                        ),
                    uncounted
                      ? '—'
                      : cell(
                          l.skusCounted,
                          <span>
                            {l.skusCounted}
                            <span className="text-xs text-slate-400"> of {l.skusAtLocation}</span>
                          </span>,
                        ),
                    uncounted ? '—' : qty(l.counted),
                    uncounted ? '—' : qty(l.systemQty),
                    uncounted
                      ? '—'
                      : cell(
                          varNum,
                          <span
                            className={
                              varNum === 0
                                ? 'text-slate-500'
                                : varNum < 0
                                  ? 'font-semibold text-alert'
                                  : 'font-semibold text-amber-700'
                            }
                          >
                            {varNum > 0 ? '+' : ''}
                            {qty(l.variance)}
                          </span>,
                        ),
                    uncounted ? '—' : l.skusDisagreeing,
                    // A variance measured against a stale balance is an artefact, not a variance.
                    l.snapshotDate === null
                      ? cell('', <span className="text-alert">no balance held</span>)
                      : cell(
                          l.snapshotDate,
                          <span
                            className={
                              (l.snapshotAgeDays ?? 0) > 2 ? 'text-alert' : 'text-slate-500'
                            }
                          >
                            {businessDate(l.snapshotDate)}
                            <span className="text-xs"> · {l.snapshotAgeDays}d old</span>
                          </span>,
                        ),
                  ];
                })}
              />
            )}
            <p className="mt-3 text-xs text-slate-500">
              Counts are not added together — a SKU can appear in many counts, so each location
              shows its latest submitted one. “SKUs counted” against the location total is how much
              was actually looked at.
            </p>
          </Card>

          <Card
            title="Stock counts"
            subtitle="RFID sweeps from the handheld. Counted quantity is what the tags actually read."
          >
            <div className="mb-4 flex flex-wrap items-end gap-3">
              <label className="text-xs text-slate-600">
                <span className="mb-1 block">Location</span>
                <select
                  value={filter.location}
                  onChange={(e) => setFilter('location', e.target.value)}
                  className="rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                >
                  <option value="">All locations</option>
                  {(counts.data?.locations ?? []).map((l) => (
                    <option key={l.dept_code} value={l.dept_code}>
                      {l.dept_code}
                      {l.dept_name ? ` — ${l.dept_name}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-slate-600">
                <span className="mb-1 block">From ({MYT})</span>
                <input
                  type="date"
                  value={filter.from}
                  max={filter.to || undefined}
                  onChange={(e) => setFilter('from', e.target.value)}
                  className="rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                />
              </label>
              <label className="text-xs text-slate-600">
                <span className="mb-1 block">To ({MYT})</span>
                <input
                  type="date"
                  value={filter.to}
                  min={filter.from || undefined}
                  onChange={(e) => setFilter('to', e.target.value)}
                  className="rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                />
              </label>
              {filterQuery && (
                <button
                  type="button"
                  onClick={() => setParams(new URLSearchParams(), { replace: true })}
                  className="rounded border border-slate-300 px-3 py-2 text-sm hover:bg-brand-50"
                >
                  Clear
                </button>
              )}
            </div>
            {counts.data?.truncated && (
              <p className="mb-3 text-xs text-slate-500">
                Showing the newest {counts.data.limit} — narrow the location or dates to see older
                counts.
              </p>
            )}
            {counts.isLoading ? (
              <Loading />
            ) : (counts.data?.counts ?? []).length === 0 ? (
              <Empty>
                {filterQuery
                  ? 'No stock counts match this location and date.'
                  : 'No stock counts yet. They appear here within 15 minutes of a submit.'}
              </Empty>
            ) : (
              <Table
                head={['Count', 'Location', `When (${MYT})`, 'Status', 'SKUs', 'Counted', '']}
                rows={(counts.data?.counts ?? []).map((c) => {
                  const st = STATUS[Number(c.status)] ?? {
                    label: String(c.status),
                    cls: 'bg-slate-100',
                  };
                  return [
                    <span className="font-mono text-xs">{c.bill_no}</span>,
                    c.dept_name ?? c.dept_code,
                    when(c.sl_created_at ?? c.planned_date),
                    <span className={`rounded px-2 py-0.5 text-xs ${st.cls}`}>{st.label}</span>,
                    String(c.skus),
                    cell(
                      Number(c.counted_qty),
                      <span className="font-medium">{qty(c.counted_qty)}</span>,
                    ),
                    // A real link, not a button: it opens in a tab, can be middle-clicked, copied, or
                    // sent to someone. The counts list is long, and expanding a panel below it meant
                    // scrolling back past every row to read the answer.
                    <a
                      href={`/rfid/stock-count/${encodeURIComponent(c.bill_no)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block rounded border border-slate-300 px-2 py-1 text-xs hover:bg-brand-50"
                    >
                      Variance ↗
                    </a>,
                  ];
                })}
              />
            )}
          </Card>
        </>
      )}

      {selected && (
        <Card
          title={`Variance — ${selected}`}
          subtitle="Every counted SKU versus SQL Accounting's system quantity, matches included. Negative means missing."
          actions={
            <a
              href={`/api/v1/stock-count/${encodeURIComponent(selected)}/variance.csv` + basisQuery}
              // Server-rendered so the file carries EVERY row, not just the 500 shown here.
              className="rounded border border-slate-300 px-3 py-1.5 text-xs font-medium hover:bg-brand-50"
            >
              Download CSV
              {basis === 'live' ? ' (live)' : basis === 'snapshot' ? ' (last night)' : ''}
            </a>
          }
        >
          {variance.isLoading ? (
            <Loading />
          ) : v?.note ? (
            <Empty>{v.note}</Empty>
          ) : (
            <>
              {/* A count compared against a stale balance is not a variance. Say so, loudly. */}
              {(v?.snapshotAgeDays ?? 0) > 1 && (
                <p className="mb-4 rounded border border-alert/40 bg-alert/10 px-4 py-2 text-sm text-brand-900">
                  <strong>Compared against a {v?.snapshotAgeDays}-day-old balance</strong> (
                  {businessDate(v?.snapshotDate)}). Differences below may be movements since that
                  date rather than real variances. Refresh the balance before acting on these
                  numbers.
                </p>
              )}

              <div className="mb-4 grid gap-4 sm:grid-cols-5">
                <Kpi label="Items counted" value={String(v?.totals?.countedSkus ?? 0)} />
                <Kpi
                  label="Counted with variance"
                  value={String(v?.totals?.countedWithVariance ?? 0)}
                  tone={v?.totals?.countedWithVariance ? 'alert' : undefined}
                />
                <Kpi
                  label="Over / short"
                  value={`${v?.totals?.over ?? 0} / ${v?.totals?.short ?? 0}`}
                />
                <Kpi label="Never scanned" value={String(v?.totals?.notCounted ?? 0)} />
              </div>

              {(v?.totals?.zeroBothHidden ?? 0) > 0 && (
                <p className="mb-4 text-sm text-slate-500">
                  {v?.totals?.zeroBothHidden} item(s) counted 0 where SQL also holds 0 are not
                  shown.
                </p>
              )}

              {(v?.totals?.notCounted ?? 0) > 0 && (
                <p className="mb-4 rounded border border-slate-200 bg-brand-50 px-4 py-2 text-sm text-slate-700">
                  <strong>This looks like a partial count.</strong> {v?.totals?.countedSkus} item(s)
                  were scanned; {v?.totals?.notCounted} more are held at this location and were
                  never read. Those appear below with a variance equal to their full system quantity
                  — that is what an unscanned SKU looks like, not a loss.
                  {v?.truncated && (
                    <>
                      {' '}
                      Showing the largest {v.truncated.shown} of {v.truncated.total}.
                    </>
                  )}
                </p>
              )}

              {/* Which balance this report is measured against, and how to change it. */}
              <div className="mb-4 flex flex-wrap items-center gap-3 rounded border border-slate-200 bg-slate-50 px-4 py-2 text-sm">
                {v?.basis === 'count-start' ? (
                  <>
                    <span className="font-medium text-brand-800">
                      <strong>System</strong> is SQL Accounting&rsquo;s stock when this count{' '}
                      {v.baselineTrigger === 'submitted'
                        ? 'was submitted'
                        : v.baselineTrigger === 'started'
                          ? 'started'
                          : 'was created'}
                      , read at {myt(v.baselineAt)} — every document entered, including a DO dated
                      later.
                      {v.baselineStartAt && (
                        <>
                          {' '}
                          <strong>At start</strong> is SQL when it started ({myt(v.baselineStartAt)}
                          ); where the two differ, stock moved or was entered during the count.
                        </>
                      )}
                    </span>
                    <button
                      type="button"
                      className="rounded border border-slate-300 px-3 py-1 font-medium hover:bg-white disabled:opacity-50"
                      disabled={variance.isFetching}
                      onClick={() => setBasis('live')}
                    >
                      Compare against SQL now{location === 'HQ1' ? ' (~30s)' : ''}
                    </button>
                  </>
                ) : basis !== 'live' ? (
                  <>
                    <span className="text-slate-700">
                      <strong>System</strong> is SQL Accounting&rsquo;s closing balance for{' '}
                      {businessDate(v?.snapshotDate)}, swept at 02:00. An edit made today is not in
                      it.
                      {basis === 'default' && ' No reading was taken when this count began.'}
                    </span>
                    <button
                      type="button"
                      className="rounded bg-brand-600 px-3 py-1 font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                      disabled={variance.isFetching}
                      onClick={() => setBasis('live')}
                    >
                      {/* HQ1 is 1,717 SKUs and ~30s against LSK's bridge; the others are 2-3s.
                          Saying so stops a slow answer reading as a hung button. */}
                      Compare against live SQL{location === 'HQ1' ? ' (~30s)' : ''}
                    </button>
                  </>
                ) : (
                  <>
                    <span className="font-medium text-brand-800">
                      Measured against <strong>live SQL Accounting</strong>
                      {v?.liveReadAt && (
                        <>
                          , read at{' '}
                          {new Date(v.liveReadAt).toLocaleTimeString('en-GB', {
                            timeZone: 'Asia/Kuala_Lumpur',
                          })}
                        </>
                      )}
                      .
                    </span>
                    <button
                      type="button"
                      className="rounded border border-slate-300 px-3 py-1 font-medium hover:bg-white disabled:opacity-50"
                      disabled={variance.isFetching}
                      onClick={() => setBasis('default')}
                    >
                      Back to the count&rsquo;s own balance
                    </button>
                    <button
                      type="button"
                      className="rounded border border-slate-300 px-3 py-1 font-medium hover:bg-white disabled:opacity-50"
                      disabled={variance.isFetching}
                      onClick={() => void variance.refetch()}
                    >
                      Re-read now
                    </button>
                  </>
                )}
                {variance.isFetching && <span className="text-slate-500">Reading…</span>}
                {variance.isError && basis === 'live' && (
                  <span className="font-medium text-alert">
                    Could not reach SQL Accounting — nothing is shown rather than last night&rsquo;s
                    figures under a live label.
                  </span>
                )}
              </div>

              {(v?.rows ?? []).length === 0 ? (
                <Empty>
                  No variance — every counted item matches the system quantity as at{' '}
                  {businessDate(v?.snapshotDate)}.
                </Empty>
              ) : (
                <Table
                  head={[
                    'Item',
                    'Group',
                    'Description',
                    'Counted',
                    // The column header carries the basis, so a screenshot of this table is still
                    // unambiguous once it has left the screen it was taken from.
                    v?.basis === 'count-start'
                      ? v.baselineTrigger === 'submitted'
                        ? 'System (at submit)'
                        : 'System (at count start)'
                      : basis === 'live'
                        ? 'System (live)'
                        : 'System (last night)',
                    'Variance',
                    'Scanned?',
                    ...(withStart ? ['At start'] : []),
                  ]}
                  rows={(v?.rows ?? []).map((r) => [
                    <span className="font-mono text-xs">{r.sku}</span>,
                    cell(
                      r.stockgroup ?? '',
                      <span className="text-xs">{r.stockgroup ?? '—'}</span>,
                    ),
                    <>
                      {r.description ?? '—'}
                      {/* The handheld reads the TAG's code; this is how it became this item. */}
                      {r.counted_as && (
                        <div className="text-xs text-slate-500">Counted as {r.counted_as}</div>
                      )}
                    </>,
                    qty(r.counted),
                    qty(r.system_qty),
                    cell(
                      Number(r.variance),
                      <span
                        className={
                          Number(r.variance) < 0
                            ? 'font-medium text-alert'
                            : 'font-medium text-brand-700'
                        }
                      >
                        {Number(r.variance) > 0 ? '+' : ''}
                        {qty(r.variance)}
                      </span>,
                    ),
                    r.was_counted ? (
                      <span className="text-emerald-700">scanned</span>
                    ) : (
                      <span className="text-slate-400">not scanned</span>
                    ),
                    ...(withStart
                      ? [
                          cell(
                            Number(r.system_at_start ?? 0),
                            <span
                              className={
                                Number(r.system_at_start ?? 0) === Number(r.system_qty)
                                  ? 'text-slate-400'
                                  : 'font-medium text-amber-700'
                              }
                            >
                              {qty(r.system_at_start ?? '0')}
                            </span>,
                          ),
                        ]
                      : []),
                  ])}
                />
              )}
            </>
          )}
        </Card>
      )}
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone?: 'alert' }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div
        className={`mt-1 text-2xl font-semibold ${tone === 'alert' ? 'text-alert' : 'text-slate-900'}`}
      >
        {value}
      </div>
    </div>
  );
}

function myt(iso: string | null | undefined): string {
  return iso
    ? new Date(iso).toLocaleString('en-GB', {
        timeZone: 'Asia/Kuala_Lumpur',
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '—';
}
